import { useTranslation } from "next-i18next";
import parse from "html-react-parser";
import Accordion from "../../components/v2/accordion/accordion";
import CardIcon from "../../components/v2/cards/card-icon";
import CardWithIcon from "../../components/v2/cards/card-with-icon";
import ProductHeroSection from "../../components/v2/common/product-hero";
import { K8s } from "../../components/v2/icons";
import ProductResources from "../../components/v2/product/product-resource";
import OpsAiSteps from "../../components/v2/tabs/ops-ai-steps";
import '../../styles/scss/_ops-ai-mttr.module.scss';
import '../../styles/scss/_ops-ai-page.module.scss';
import '../../styles/scss/_get-started-ops-ai.module.scss';
import '../../styles/scss/_faq-section.module.scss';
import ProductOverviewSection from "../../components/v2/product-overview-section";
import { APM, APMIcon, RUMIcon, GoesBeyondAlerting, GrafanaDatadog, IngestsAlertsDatadogGrafana, Logs, RespectsYourPrivacy, ShipsActualCode, SreAgent, WorksAcrossYourStack, InfrastructureIcon, DatadogLogo, GrafanaLogo } from "../../components/v2/icons-v2";
import ClientTabsTestimonials from "../../components/v2/mw-carousel/testimonial-with-tabs";
import ProductReadyToStart from "../../components/v2/product/product-ready-to-start";
import Image from "next/image";
import { useState } from "react";
import Dialog from "../../components/v2/dialog";
import HomeFeaturedTestimonials from "../../components/home/home-featured-testimonials";

export default function OpsAi() {
    const { t } = useTranslation("common");

    const faqsData = t('product.ops-ai.faq.questions', { returnObjects: true }) as any[]
    const faqs = Array.isArray(faqsData) ? faqsData : []

    const resourceData = t('product.ops-ai.resources', { returnObjects: true }) as any[]
    const resource = Array.isArray(resourceData) ? resourceData : []

    const detailedFeatures = t('product.ops-ai.section.detailedFeatures', { returnObjects: true }) as any[]

    const [imageOpen, setImageOpen] = useState(false);
    const [image, setImage] = useState<string | null>(null);


    const itemList = [
        {
            title: "Real User Monitoring (RUM)",
            link: "https://docs.middleware.io/rum/rum-overview",
            icon: <RUMIcon />,
        },
        {
            title: "Application Performance Monitoring (APM)",
            link: "https://docs.middleware.io/apm-configuration/apm_overview",
            icon: <APMIcon />,
        },
        {
            title: "Kubernetes",
            link: "https://docs.middleware.io/agent-installation/kubernetes",
            icon: <K8s />,
        },
        {
            title: "Datadog",
            link: "https://docs.middleware.io/integrations/datadog-integration",
            icon: <DatadogLogo />,
        },
        {
            title: "Grafana",
            link: "https://docs.middleware.io/integrations/grafana-integration",
            icon: <GrafanaLogo />,
        }
    ]

    const productOverviewData = {
        title: "Product overview",
        buttons: [
            {
                label: "View Docs",
                link: "https://docs.middleware.io/opsai/opsai_overview",
                className: "ahref-btn primary",
            },
            {
                label: "View Pricing",
                link: "/pricing/",
                className: "ahref-btn secondary",
            },
        ],
        description:
            "Engineering teams waste countless hours context-switching and correlating data to troubleshoot production issues. Middleware OpsAI replaces this complex workflow by detecting backend, frontend, and Kubernetes issues in real time. By tracing issues to the exact line of code and automatically opening pull requests with fixes, OpsAI enables end-to-end resolution so your team can focus on shipping features instead of fighting fires.",
        worksWellWithTitle: "Works Well With",
        worksWellWith: [
            {
                title: "Logs",
                link: "/product/log-monitoring/",
                icon: <Logs />
            },
            {
                title: "Infrastructure",
                link: "/product/infrastructure-monitoring/",
                icon: <InfrastructureIcon />
            },
            {
                title: "APM",
                link: "/product/apm/",
                icon: <APM />
            },
        ],
    };

    const handleImageClick = (src: string, alt?: string) => {
        setImage(src);
        setImageOpen(true);
    };

    const [testimonialVideoOpen, setTestimonialVideoOpen] = useState(false);
    const [testimonialVideoSrc, setTestimonialVideoSrc] = useState<string | null>(
        null
    );
    const [isNotVideo, setIsNotVideo] = useState(false);

    const handleTestimonialVideoOpen = (src: string, isNotVideo?: boolean) => {
        setTestimonialVideoSrc(src);
        setTestimonialVideoOpen(true);
        setIsNotVideo(isNotVideo);
    };

    return (
        <>

            <ProductHeroSection title={<h1>{t('product.ops-ai.hero.title')} <span>{t('product.ops-ai.hero.titleHighlight')}</span></h1>}
                description={t('product.ops-ai.hero.description')}
                img={"/images/product/ops-ai/ops-ai-hero-image.webp"} imgMobile={"/images/v2/products/ops-ai-hero-image-mobile.webp"} imgAlt={t('product.ops-ai.hero.imageAlt')}
                hasActions={true} tips={t('product.hero.tips')} />

            <ProductOverviewSection productOverviewData={productOverviewData} />

            <section className={"ops-ai-mttr"}>
                <div className={"container"}>
                    <div className={"ops-ai"}>
                        <div className="d-flex">
                            <h2>{t('product.ops-ai.section.title')}</h2>
                            <p>{t('product.ops-ai.section.description')}</p>
                        </div>
                        <section className={'mw-card career'}>
                            <div className={'d-flex card-items'}>
                                <CardWithIcon
                                    icon={<GoesBeyondAlerting />}
                                    title={"Goes beyond alerting"}
                                    description={
                                        "OpsAI takes the alert, pulls in stack traces, logs, error metadata, and your source code through a secure GitHub MCP connection, then generates a targeted fix."
                                    }
                                />
                                <CardWithIcon
                                    icon={<WorksAcrossYourStack />}
                                    title={"Works across your entire stack "}
                                    description={
                                        "Backend errors from APM, frontend crashes from RUM, and Kubernetes pod failures are all managed in one place."
                                    }
                                />
                                <CardWithIcon
                                    icon={<IngestsAlertsDatadogGrafana />}
                                    title={"Ingests alerts from Datadog and Grafana"}
                                    description={
                                        "Already using other monitoring tools? OpsAI ingests alerts, investigates issues using their metrics and logs, and provides diagnoses directly inside Middleware."
                                    }
                                />
                                <CardWithIcon
                                    icon={<ShipsActualCode />}
                                    title={"Ships actual code"}
                                    description={
                                        "When confidence is high, OpsAI opens a pull request with a clean diff. For Kubernetes, it can apply fixes directly with zero intervention."
                                    }
                                />
                                <CardWithIcon
                                    icon={<RespectsYourPrivacy />}
                                    title={"Respects your privacy"}
                                    description={
                                        "OpsAI reads only the files related to a specific error via GitHub MCP. It never scans your entire codebase and does not store your source code."
                                    }
                                />

                            </div>
                        </section>

                        <h3>From detection to resolution — see Middleware in action</h3>

                        <div className="ops-ai-video">
                            <video
                                playsInline
                                autoPlay
                                muted
                                loop
                                preload="auto"
                                width="100%"
                                height={450}
                                controls={false}
                            >
                                <source src={"/images/opsai-cut-mttr.mp4"} type="video/mp4" />
                            </video>
                        </div>


                    </div>
                </div>
            </section>

            <div className={"get-start-opsai"}>
                <div className={"container"}>
                    <h2>{t('product.ops-ai.section.getStarted')} <span className={"text-gradient"}></span> </h2>
                    <p className={"description"}>{t('product.ops-ai.section.getStartedDesc')}</p>
                </div>
                <OpsAiSteps />
            </div>

            <section className="ops-ai-features">
                <div className={"container"}>
                    <h2>{t('product.ops-ai.section.features')}</h2>
                    <div className="d-flex">
                        {detailedFeatures && detailedFeatures.length > 0 && (
                            <>
                                <CardIcon title={detailedFeatures[0]?.title || ''} icon="/images/product/ops-ai/icon/error-fixing.svg" description={detailedFeatures[0]?.description || ''} />

                                <CardIcon title={detailedFeatures[1]?.title || ''} icon="/images/product/ops-ai/icon/k8s-debugging.svg" description={detailedFeatures[1]?.description || ''} />

                                <CardIcon title={detailedFeatures[2]?.title || ''} icon="/images/product/ops-ai/icon/third-party-alert.svg" description={detailedFeatures[2]?.description || ''} />

                                <CardIcon title={detailedFeatures[3]?.title || ''} icon="/images/product/ops-ai/icon/logs-pattern.svg" description={detailedFeatures[3]?.description || ''} />

                                <CardIcon title={detailedFeatures[4]?.title || ''} icon="/images/product/ops-ai/icon/anomaly-detection.svg" description={detailedFeatures[4]?.description || ''} />
                            
                                <CardIcon title={detailedFeatures[5]?.title || ''} icon="/images/product/ops-ai/icon/fix-with-slack.svg" description={detailedFeatures[5]?.description || ''} />
                            </>
                        )}
                    </div>
                </div>
            </section>

            <ProductReadyToStart productReadyToStart={itemList} />


            <section className="testimonial-section">
                <div className="container">
                    <div className="section-tag testimonial">{t("testimonials.tag")} 👤</div>
                    <h2>Why <span>Organizations</span> Trust Middleware </h2>
                    <HomeFeaturedTestimonials
                        onVideoOpen={handleTestimonialVideoOpen}
                    />

                    <ClientTabsTestimonials />
                </div>
            </section>

            <ProductResources resources={resource} title={t('product.ops-ai.resourcesTitle')} />

            <section className={"faq-section"}>
                <div className={"container"}>
                    <h2>{t('product.ops-ai.faq.title')}</h2>
                    <p>{t('product.ops-ai.faq.subtitle')}</p>
                    <div className={"faq-item"}>
                        <Accordion faqs={faqs.map(faq => ({
                            ...faq,
                            answerPlain: faq.text,
                            text:
                                faq.textHtml != null && faq.textHtml !== ""
                                    ? parse(faq.textHtml)
                                    : <p>{faq.text}</p>
                        }))} showGroups={true} />
                    </div>
                </div>
            </section>

            <Dialog
                isOpen={testimonialVideoOpen}
                position="center"
                onClose={() => {
                    setTestimonialVideoOpen(false);
                    setTestimonialVideoSrc(null);
                }}
                size={isNotVideo ? "sx" : "md"}
                verticalCenter={true}
            >
                <div style={{ textAlign: "center" }}>
                    {testimonialVideoSrc && !isNotVideo ? (
                        <video
                            key={testimonialVideoSrc}
                            playsInline
                            preload="metadata"
                            autoPlay
                            width="100%"
                            height={450}
                            controls={true}
                        >
                            <source src={testimonialVideoSrc} type="video/mp4" />
                        </video>
                    ) : isNotVideo ? <Image alt={"Testimonials Middleware"} height={520} width={360} src={testimonialVideoSrc} /> : null}
                </div>
            </Dialog>


            <Dialog
                isOpen={imageOpen}
                position="center"
                onClose={() => setImageOpen(false)}
                size="md"
                verticalCenter={true}
            >
                <div style={{ textAlign: "center" }}>
                    <video
                        playsInline
                        preload="auto"
                        autoPlay
                        width="100%"
                        height={450}
                        controls={true}
                    >
                        <source src={image} type="video/mp4" />
                    </video>
                </div>
            </Dialog>
        </>
    )
}